import {
    MouseEvent,
    useState,
    useEffect,
    CSSProperties,
} from "react";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { useHistory } from "react-router";
import { CurrentBrand, OrganizationStatus } from "modules/Types";
import ProfileIcon from "assets/images/profileIcon.svg";
import CustomAvatar from "./CustomAvatar";
import SearchSelector from "./AdminSearchSelectorModal";
import { Search } from "@material-ui/icons";
import useOrganization from 'Hooks/useOrganization';
import Sty, { Container, StyledLabelSpan } from "modules/Sty";
import useBrand, { checkTier } from 'Hooks/useBrand';
import useUser from 'Hooks/useUser';
import { clsx, deepCopy, makeKey } from "modules/Utils";
import ButtonSymphony from "elements/ButtonSymphony";
import { PlanType, USER_PERMISSION_ON_BRAND_INFO } from "modules/Const";
import { BrandTierType } from "elements/BrandTier";
import BrandTier from "elements/BrandTier";
import Colors from "modules/Colors";
import Icon from "elements/Icon";
import useOrganizationV2 from "Hooks/useOrganizationV2";
import AdminId from "elements/AdminId";

const sortAlphabetically = (brands: CurrentBrand[]) => {
    const sortedBrands = deepCopy(brands);
    return (
        sortedBrands.sort(
            (a: CurrentBrand, b: CurrentBrand) =>
                a.name
                    ?.toLowerCase()
                    .localeCompare(b.name?.toLowerCase() as string) as number
        )
    );
}

interface BrandDropdownProps {
    className: string;
    smallSidebar?: boolean;
}

const BrandDropdown = ({
    className,
    smallSidebar,
}: BrandDropdownProps) => {
    const history = useHistory();
    const { isSymphonyAdmin } = useUser()
    const {
        currentBrand,
        loadedBrands,
        setCurrentBrand,
        getBrand,
        getBrandImage,
        resetDifferentCurrentBrand
    } = useBrand();
    const {
        organization,
        organizationBrandIds,
        // brandIdIsInOrganization
        // } = useOrganization()
    } = useOrganizationV2()

    const opacity = currentBrand ? 1 : 0;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [brandImage, setBrandImage] = useState<string | null>(getBrandImage() || null);
    const [openAdminSelector, setOpenAdminSelector] = useState<boolean>(false)

    useEffect(() => {
        if (currentBrand) {
            setBrandImage(getBrandImage())
        }
    }, [currentBrand])

    const handleOpenDropdown = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    }

    const handleCloseDropdown = () => {
        setAnchorEl(null)
    }

    const handleClickAddArtist = () => {
        history.push('/add-artist');
    }

    const handleClickBrandFromDropdown = async (brand: CurrentBrand) => {
        // NOTE: this executes when you click on a brand (artist) in the dropdown
        handleCloseDropdown();
        const isDifferentBrand = brand.id !== currentBrand?.id;
        try {
            if (isDifferentBrand) {
                resetDifferentCurrentBrand()
            }

            const updatedBrandData = await getBrand(brand.id)
            setCurrentBrand(updatedBrandData);

            if (window.location.pathname.includes('/campaigns')) {
                history.push(`/marketing`)
            }
        } catch (error) {
            console.error('Error updating brand:', error);
        }
    };

    const renderBrandsList = () => {
        if (!loadedBrands || loadedBrands.length === 0) {
            return null
        }
        const sortedBrands = sortAlphabetically(loadedBrands).map((currentBrandLocal: CurrentBrand, index: number) => {
            const { name, image, permissionLevel } = currentBrandLocal;
            const tier = checkTier(currentBrandLocal)

            const isBrandInOrganization = organizationBrandIds?.includes(currentBrandLocal.id)

            return (
                <div
                    key={makeKey(name, index)}
                    className="menuItemProfileDropdown"
                    style={sty.menuItemProfileDropdown}
                    onClick={() => handleClickBrandFromDropdown(currentBrandLocal)}
                >
                    <CustomAvatar
                        className="fromBrandDropdownCustomAvatar"
                        image={image}
                        isBorder={image ? false : true}
                        brandOverride={currentBrandLocal}
                    />
                    <div className="brandDropdownMenuItemsContent">
                        <div
                            className="brandNameProfileDropdown"
                            style={sty.brandNameProfileDropdown}
                        >
                            {name}
                        </div>
                        {organization && organization.setup_status === OrganizationStatus.READY && isBrandInOrganization &&
                            <>
                                <div
                                    className="organizationNameProfileDropdown"
                                    style={{
                                        ...Sty.text12,
                                        color: Colors.goldDark,
                                    }}
                                >
                                    {organization?.name}
                                </div>
                                <div
                                    className="permissionLevelProfileDropdown"
                                    style={{
                                        ...Sty.text12,
                                        color: USER_PERMISSION_ON_BRAND_INFO[permissionLevel]?.color,
                                    }}
                                >
                                    {USER_PERMISSION_ON_BRAND_INFO[permissionLevel]?.title}
                                </div>
                            </>
                        }
                        <AdminId
                            className="adminIdProfileDropdown1"
                            id={currentBrandLocal.id}
                        />
                    </div>
                    <BrandTier
                        className="brandTierProfileDropdown1"
                        style={{ marginLeft: 'auto' }}
                        tier={tier}
                        type={BrandTierType.TEXT}
                        iconSize={14}
                        textSize={12}
                        isWhiteBackground
                    />
                </div>
            );
        })

        return (
            <div className="brandDropdownMenuItems" style={{ minWidth: 280 }}>
                {isSymphonyAdmin &&
                    <MenuItem
                        key="admin-search"
                        className="adminSearchBrandProfile"
                        style={sty.menuItemProfileDropdown}
                        onClick={() => {
                            setOpenAdminSelector(true)
                            handleCloseDropdown()
                        }}
                    >
                        <Container>
                            <Search />
                        </Container>
                        <Container display="flex" flexDirection="column" alignItems="flex-start" width="100%">
                            <StyledLabelSpan
                                padding="0px 0px 0px 16px"
                                fontSize={16}
                                fontWeight={400}
                            >
                                Open Admin Search
                            </StyledLabelSpan>
                            {isSymphonyAdmin &&
                                <StyledLabelSpan
                                    paddingLeft="16px"
                                    color="#E08507"
                                    fontSize={12}
                                    fontWeight={400}
                                >
                                    Search for any artist as an admin.
                                </StyledLabelSpan>
                            }
                        </Container>
                    </MenuItem>
                }
                {sortedBrands}
            </div>
        )
    }

    // NOTE: render()
    return (
        <>
            <style>
                {`
                    .mainAddNewBrandButton, .menuItemProfileDropdown {
                        &:hover {
                        background-color: rgba(0,0,0,0.05) !important;
                        }
                    }
                `}
            </style>

            {/* NOTE: this is the Dropdown Button */}
            <div
                className={clsx('brandDropdownComponent', className)}
                style={{
                    cursor: 'pointer',
                    opacity: opacity,
                    borderRadius: 16,
                    paddingTop: 4,
                    paddingBottom: 4,
                }}
            >
                <ButtonSymphony
                    className="brandDropdownButton"
                    style={sty.brandDropdownButton}
                    styleButtonContent={{
                        width: '100%',
                    }}
                    onClick={handleOpenDropdown} // NOTE: this opens brandDropdownMenu
                >
                    <CustomAvatar
                        className="avatarUserStatusProfileDropdown"
                        image={brandImage ? brandImage : ProfileIcon}
                        isBorder={brandImage ? false : true}
                        brandOverride={currentBrand}
                    />
                    <div>
                        <div style={Sty.text16white}>
                            {currentBrand?.name}
                        </div>
                        <div className="brandDropdownTierContainer" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <BrandTier
                                className="brandTierProfileDropdown"
                                tier={currentBrand?.tier as PlanType}
                                type={BrandTierType.TEXT}
                            />
                            <AdminId
                                className="adminIdProfileDropdown2"
                                id={currentBrand?.id}
                            />
                        </div>
                    </div>
                    <Icon
                        className="brandDropdownIcon"
                        style={{ opacity: 0.5, marginLeft: 'auto' }}
                        icon="chevronDown"
                        color={Colors.white}
                        size={16}
                    />
                </ButtonSymphony>

                {/* NOTE: this is the Menu pop up */}
                <Menu
                    id="simple-menu"
                    className="brandDropdownMenu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleCloseDropdown}
                    MenuListProps={{
                        style: {
                            padding: 0,
                            maxWidth: 400
                        },
                    }}
                >
                    {loadedBrands &&
                        renderBrandsList()
                    }
                    <ButtonSymphony
                        key="add-new-brand"
                        className="mainAddNewBrandButton"
                        style={sty.mainAddNewBrandButton}
                        onClick={handleClickAddArtist}
                        variant="text"
                    >
                        + Add New Brand
                    </ButtonSymphony>
                </Menu>
                {isSymphonyAdmin &&
                    <SearchSelector
                        className="fromBrandDropdownSearchSelector"
                        mode="default"
                        open={openAdminSelector}
                        onClose={() => {
                            setOpenAdminSelector(false)
                            handleCloseDropdown()
                        }}
                    />
                }
            </div>
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    mainAddNewBrandButton: {
        display: 'flex',
        width: '100%',
        paddingTop: 18,
        paddingBottom: 16,
        borderRadius: 0,
    },
    brandDropdownButton: {
        backgroundColor: 'transparent',
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 6,
        paddingBottom: 6,
        height: 'auto',
        textAlign: 'left',
        justifyContent: 'flex-start',
        width: '100%',
    },
    brandDropdownUserNameText: {
        color: "white",
        fontSize: 16,
        textAlign: "left",
        padding: "0 4px 0 4px",
        width: "120px",
        height: "56px"
    },
    menuItemProfileDropdown: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 8,
        cursor: 'pointer',
        minHeight: 67,
        borderBottom: Sty.border,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 16,
        paddingRight: 16,
    },
    brandNameProfileDropdown: {
        fontSize: 16,
        fontWeight: 400,
        width: '100%',
    },
}

export default BrandDropdown;
